import { useState } from 'react';
import { format } from 'date-fns';
import { Loader2, Activity } from 'lucide-react';
import { useDepartmentActivity } from '@/hooks/useDepartments';

const ACTION_LABELS = {
  create: 'Created',
  update: 'Updated',
  archive: 'Archived',
  restore: 'Restored',
  owner_change: 'Owner Changed',
  member_add: 'Member Added',
  member_remove: 'Member Removed',
};

const ACTION_COLORS = {
  create: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  update: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  archive: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  restore: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  owner_change: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  member_add: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300',
  member_remove: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
};

const DepartmentActivityTab = ({ departmentId }) => {
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isError } = useDepartmentActivity(departmentId, { page, limit });

  const activities = data?.activities || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading activity...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-10 text-red-500 text-sm">
        Failed to load activity. Please try again.
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-gray-400">
        <Activity className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">No activity recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {activities.map((activity) => (
        <div key={activity._id} className="flex gap-3 text-sm">
          {/* Timeline dot */}
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 flex-shrink-0" />
            <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 mt-1" />
          </div>

          <div className="pb-3 flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ACTION_COLORS[activity.actionType] || 'bg-gray-100 text-gray-600'}`}>
                {ACTION_LABELS[activity.actionType] || activity.actionType}
              </span>
              <span className="text-xs text-gray-400">
                {activity.createdAt
                  ? format(new Date(activity.createdAt), 'MMM dd, yyyy HH:mm')
                  : '—'}
              </span>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mt-0.5">{activity.description}</p>
            {activity.performedBy && (
              <p className="text-xs text-gray-400 mt-0.5">
                by {activity.performedBy.name || activity.performedBy.email || 'Unknown'}
              </p>
            )}
          </div>
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 border-t text-xs text-gray-500">
          <span>{total} total</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2 py-1 rounded border disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Prev
            </button>
            <span className="px-2 py-1">{page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2 py-1 rounded border disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentActivityTab;
